/**
 * Main Product Section - JavaScript
 * Silverback Moto Co.
 *
 * Fixes applied:
 * - openLightbox now sets the inner transform BEFORE making the lightbox visible.
 *   The previous setTimeout(50) approach left a 50ms window where slide 0 painted
 *   regardless of the requested startIndex, which produced "lightbox opens on the
 *   wrong image" symptoms on mobile. translate3d is used to force GPU compositing
 *   and avoid sub-pixel rounding from vw units.
 * - lightboxContent.scrollLeft / scrollTop are explicitly reset on open so any
 *   stale scroll state from a previous interaction does not leak into the new view.
 */

(function() {
  'use strict';

  const section = document.querySelector('[data-product-section]');
  if (!section) return;

  const productJSON = section.querySelector('[data-product-json]');
  const product = productJSON ? JSON.parse(productJSON.textContent) : null;

  // Elements
  const galleryScroll = section.querySelector('[data-gallery-scroll]');
  const productInfo = section.querySelector('[data-product-info]');
  const carouselTrack = section.querySelector('[data-carousel-track]');
  const progressBar = section.querySelector('[data-progress-bar]');
  const mediaItems = section.querySelectorAll('[data-media-item]');
  const slides = section.querySelectorAll('[data-slide]');
  const lightbox = section.querySelector('[data-lightbox]');
  const lightboxContent = section.querySelector('[data-lightbox-content]');
  const lightboxClose = section.querySelector('[data-lightbox-close]');
  const accordions = section.querySelectorAll('[data-accordion]');
  const productForm = section.querySelector('[data-product-form]');
  const variantInput = section.querySelector('[data-variant-input]');
  const addToCartBtn = section.querySelector('[data-add-to-cart]');
  const addToCartText = section.querySelector('[data-add-to-cart-text]');
  const priceDisplay = section.querySelector('[data-product-price]');
  const stickyBar = document.querySelector('[data-sticky-bar]');
  const stickyAddToCart = document.querySelector('[data-sticky-add-to-cart]');
  const stickyPrice = document.querySelector('[data-sticky-price]');

  let currentSlide = 0;
  let touchStartX = 0;
  let touchEndX = 0;
  let isDragging = false;

  // =====================
  // GALLERY HEIGHT SYNC
  // =====================
  function syncGalleryHeight() {
    if (window.innerWidth < 1024 || !productInfo || !galleryScroll) return;
    
    const infoHeight = productInfo.offsetHeight;
    galleryScroll.style.maxHeight = infoHeight + 'px';
    galleryScroll.style.overflowY = 'auto';
  }

  function initGalleryHeightSync() {
    if (window.innerWidth < 1024) return;
    
    // Initial sync
    syncGalleryHeight();
    
    // Watch for info panel size changes (accordion toggle)
    if ('ResizeObserver' in window && productInfo) {
      const observer = new ResizeObserver(() => {
        syncGalleryHeight();
      });
      observer.observe(productInfo);
    }
    
    // Re-sync on window resize
    window.addEventListener('resize', debounce(syncGalleryHeight, 100));
  }

  // =====================
  // MOBILE CAROUSEL
  // =====================
  function initMobileCarousel() {
    if (!carouselTrack || slides.length === 0) return;

    const totalSlides = slides.length;

    // Tap to open lightbox on mobile slides
    slides.forEach((slide, index) => {
      slide.addEventListener('click', (e) => {
        // Only open lightbox if it wasn't a swipe
        if (!isDragging && Math.abs(touchStartX - touchEndX) < 10) {
          openLightbox(index);
        }
      });
    });

    carouselTrack.addEventListener('touchstart', (e) => {
      touchStartX = e.touches[0].clientX;
      touchEndX = e.touches[0].clientX;
      isDragging = true;
    }, { passive: true });

    carouselTrack.addEventListener('touchmove', (e) => {
      if (!isDragging) return;
      touchEndX = e.touches[0].clientX;
    }, { passive: true });

    carouselTrack.addEventListener('touchend', () => {
      if (!isDragging) return;
      isDragging = false;
      const diff = touchStartX - touchEndX;
      if (Math.abs(diff) > 50) {
        handleSwipe(totalSlides);
      }
    });

    // Mouse events
    carouselTrack.addEventListener('mousedown', (e) => {
      touchStartX = e.clientX;
      isDragging = true;
      carouselTrack.style.cursor = 'grabbing';
    });

    carouselTrack.addEventListener('mousemove', (e) => {
      if (!isDragging) return;
      touchEndX = e.clientX;
    });

    carouselTrack.addEventListener('mouseup', () => {
      if (!isDragging) return;
      isDragging = false;
      carouselTrack.style.cursor = '';
      handleSwipe(totalSlides);
    });

    carouselTrack.addEventListener('mouseleave', () => {
      if (isDragging) {
        isDragging = false;
        carouselTrack.style.cursor = '';
      }
    });
  }

  function handleSwipe(totalSlides) {
    const diff = touchStartX - touchEndX;
    const threshold = 50;

    if (Math.abs(diff) > threshold) {
      if (diff > 0) {
        goToSlide((currentSlide + 1) % totalSlides);
      } else {
        goToSlide((currentSlide - 1 + totalSlides) % totalSlides);
      }
    }
    touchStartX = 0;
    touchEndX = 0;
  }

  function goToSlide(index) {
    currentSlide = index;
    if (carouselTrack) {
      carouselTrack.style.transform = `translateX(-${index * 100}%)`;
    }
    slides.forEach((slide, i) => {
      slide.classList.toggle('product-gallery__slide--active', i === index);
    });
    updateProgress();
  }

  function updateProgress() {
    if (!progressBar || slides.length === 0) return;
    const progress = ((currentSlide + 1) / slides.length) * 100;
    progressBar.style.width = `${progress}%`;
  }

  // =====================
  // DESKTOP GALLERY
  // =====================
  function initDesktopGallery() {
    if (!mediaItems.length) return;

    mediaItems.forEach((item, index) => {
      item.addEventListener('click', () => {
        openLightbox(index);
      });
    });
  }

  // =====================
  // LIGHTBOX
  // =====================
  // Carousel architecture: each .product-lightbox__item is absolutely
  // positioned (mobile) or flex-column'd (desktop). Mobile navigation sets a
  // transform per item based on (item-index − currentIndex). Loading is
  // JS-driven via data-src so we don't crush the network with 14 parallel
  // high-res fetches.
  //
  // Zoom mode: triggered by double-tap (mobile), click on slide (desktop), or
  // pinch-in (mobile). When active, only the focused slide is visible — every
  // other slide drops to opacity 0 (solo focus). The focused slide's <img>
  // gets transform: translate(x,y) scale(s) managed by pinch (mobile) or wheel
  // (desktop). Drag pans within the scaled image. Swipe is suppressed while
  // zoomed so adjacent slides don't bleed in.
  let lightboxCurrentSlide = 0;
  let lightboxTotalSlides = 0;
  let lbTouchStartX = 0;
  let lbTouchMoveX = 0;
  let lbIsSwiping = false;
  let progressiveLoadStarted = false;

  // Zoom state — global pointers; per-slide scale/pan lives on slide.__zoomState
  let zoomedSlide = null;
  let pinchActive = false;
  let pinchInitialDist = 0;
  let pinchInitialScale = 1;
  let panTouchActive = false;
  let panTouchLastX = 0;
  let panTouchLastY = 0;
  let panMouseStart = null;       // { x, y } at mousedown
  let mouseDraggedDistance = 0;   // accumulated drag pixels — distinguishes click from drag

  function getLightboxInner() {
    return lightbox ? lightbox.querySelector('[data-lightbox-inner]') : null;
  }

  // ---- Zoom helpers ----------------------------------------------------------

  function getZoomState(slide) {
    if (!slide.__zoomState) {
      slide.__zoomState = { scale: 1, panX: 0, panY: 0 };
    }
    return slide.__zoomState;
  }

  function applyZoomTransform(slide) {
    const s = getZoomState(slide);
    const img = slide.querySelector('img');
    if (!img) return;
    img.style.transform =
      'translate3d(' + s.panX + 'px, ' + s.panY + 'px, 0) scale(' + s.scale + ')';
  }

  function enterZoomMode(slide) {
    if (zoomedSlide === slide) return;
    if (zoomedSlide) exitZoomMode();
    zoomedSlide = slide;
    slide.classList.add('is-zoom-active');
    if (lightboxContent) lightboxContent.classList.add('has-zoom-active');
  }

  function exitZoomMode() {
    if (!zoomedSlide) return;
    const s = getZoomState(zoomedSlide);
    s.scale = 1;
    s.panX = 0;
    s.panY = 0;
    applyZoomTransform(zoomedSlide);
    zoomedSlide.classList.remove('is-zoom-active');
    if (lightboxContent) lightboxContent.classList.remove('has-zoom-active');
    zoomedSlide = null;
  }

  function isInZoomMode() {
    return zoomedSlide !== null;
  }

  function setSlideZoomScale(slide, newScale) {
    const s = getZoomState(slide);
    s.scale = Math.max(0.7, Math.min(8, newScale));
    applyZoomTransform(slide);
    // Auto-exit if user shrinks back to ~1
    if (s.scale <= 1.05 && zoomedSlide === slide) {
      exitZoomMode();
    }
  }

  function panSlide(slide, dx, dy) {
    const s = getZoomState(slide);
    s.panX += dx;
    s.panY += dy;
    applyZoomTransform(slide);
  }

  function getPinchDistance(t1, t2) {
    const dx = t1.clientX - t2.clientX;
    const dy = t1.clientY - t2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  // Zooms a slide's <img> to natural pixel size (1:1) — or 2x, whichever is
  // larger. This is what the user explicitly asked for: "expand to image width
  // upon double tap" so they can pinch further and pan to explore.
  function triggerZoomToFit(slide) {
    const img = slide.querySelector('img');
    if (!img) return;
    const displayW = img.getBoundingClientRect().width;
    const naturalW = img.naturalWidth;
    let targetScale = 2;
    if (naturalW > 0 && displayW > 0) {
      targetScale = Math.max(2, naturalW / displayW);
    }
    targetScale = Math.min(5, targetScale);
    const s = getZoomState(slide);
    s.scale = targetScale;
    s.panX = 0;
    s.panY = 0;
    enterZoomMode(slide);
    applyZoomTransform(slide);
  }

  // Clears inline transform/transition on items and inline transform on imgs.
  // Used on every open and close so we never inherit stale styles from a prior
  // mobile-mode interaction. This is the desktop layout fix.
  function clearAllLightboxItemStyles() {
    if (!lightboxContent) return;
    lightboxContent.querySelectorAll('[data-lightbox-item]').forEach(item => {
      item.style.transform = '';
      item.style.transition = '';
      const img = item.querySelector('img');
      if (img) img.style.transform = '';
    });
  }

  // Promotes a single slide's data-src/data-srcset/data-sizes to real
  // attributes, kicking off the actual network fetch. Idempotent: skips if
  // src is already set. Clears the data-loading flag (which drives the
  // spinner pseudo-element) once the file actually arrives.
  function ensureLightboxSlideLoaded(index) {
    if (!lightboxContent) return;
    const items = lightboxContent.querySelectorAll('[data-lightbox-item]');
    const item = items[index];
    if (!item) return;
    const img = item.querySelector('img');
    if (!img) return;
    if (img.getAttribute('src')) return; // already triggered

    if (img.dataset.srcset) img.setAttribute('srcset', img.dataset.srcset);
    if (img.dataset.sizes) img.setAttribute('sizes', img.dataset.sizes);
    if (img.dataset.src) img.setAttribute('src', img.dataset.src);

    const clearLoading = () => item.removeAttribute('data-loading');
    if (img.complete && img.naturalWidth > 0) {
      clearLoading();
    } else {
      img.addEventListener('load', clearLoading, { once: true });
      img.addEventListener('error', clearLoading, { once: true });
    }
  }

  function loadLightboxSlidesNear(currentIndex, range = 2) {
    for (let offset = -range; offset <= range; offset++) {
      const idx = currentIndex + offset;
      if (idx >= 0 && idx < lightboxTotalSlides) {
        ensureLightboxSlideLoaded(idx);
      }
    }
  }

  function loadAllLightboxSlides() {
    for (let i = 0; i < lightboxTotalSlides; i++) {
      ensureLightboxSlideLoaded(i);
    }
  }

  function startProgressiveLightboxLoad() {
    if (progressiveLoadStarted) return;
    progressiveLoadStarted = true;
    let i = 0;
    function loadNext() {
      if (i >= lightboxTotalSlides) return;
      ensureLightboxSlideLoaded(i);
      i++;
      setTimeout(loadNext, 200); // stagger so we never have >5 in flight
    }
    loadNext();
  }

  // Sets per-item transforms based on each item's offset from currentIndex.
  // animate=false skips the CSS transition (used for initial open + close
  // reset). animate=true uses the default 0.3s transition for smooth swipes.
  function updateLightboxItemPositions(currentIndex, animate = true) {
    if (!lightboxContent) return;
    const items = lightboxContent.querySelectorAll('[data-lightbox-item]');

    items.forEach((item, i) => {
      const offset = i - currentIndex;
      let translatePct;
      if (offset === 0) translatePct = 0;
      else if (offset === -1) translatePct = -100;
      else if (offset === 1) translatePct = 100;
      else if (offset < -1) translatePct = -200;
      else translatePct = 200;

      item.style.transition = animate ? '' : 'none';
      item.style.transform = 'translate3d(' + translatePct + '%, 0, 0)';
    });

    if (!animate) {
      // Force reflow so the transition: none takes effect, then restore
      // transitions on the next frame so subsequent navigation animates.
      void lightboxContent.offsetHeight;
      requestAnimationFrame(() => {
        items.forEach(item => { item.style.transition = ''; });
      });
    }
  }

  // Pixel-based per-item transform during a touch drag. Only items within
  // ±1 of current get adjusted — far items stay parked at ±200%.
  function applyLightboxDragOffset(currentIndex, dragPx) {
    if (!lightboxContent) return;
    const items = lightboxContent.querySelectorAll('[data-lightbox-item]');
    const vw = window.innerWidth;

    items.forEach((item, i) => {
      const offset = i - currentIndex;
      if (Math.abs(offset) <= 1) {
        const offsetPx = (offset * vw) + dragPx;
        item.style.transition = 'none';
        item.style.transform = 'translate3d(' + offsetPx + 'px, 0, 0)';
      }
    });
  }

  function openLightbox(startIndex = 0) {
    if (!lightbox || !lightboxContent) return;

    // Clean slate — exit any prior zoom and clear leftover inline styles.
    // Critical for desktop: a prior mobile-mode session leaves items with
    // inline transform: translate3d(±X%, 0, 0), which keeps them off-screen
    // when the desktop flex-column layout takes over.
    if (isInZoomMode()) exitZoomMode();
    clearAllLightboxItemStyles();

    const items = lightboxContent.querySelectorAll('[data-lightbox-item]');
    lightboxTotalSlides = items.length;
    lightboxCurrentSlide = Math.max(0, Math.min(startIndex, lightboxTotalSlides - 1));

    const isMobileViewport = window.innerWidth < 1024;

    if (isMobileViewport) {
      // Position items WITHOUT animation before the lightbox becomes visible.
      // Eliminates flash of stacked content on first paint.
      updateLightboxItemPositions(lightboxCurrentSlide, false);
      lightboxContent.scrollLeft = 0;
      lightboxContent.scrollTop = 0;
    }

    lightbox.classList.add('is-open');
    document.body.style.overflow = 'hidden';

    if (isMobileViewport) {
      updateLightboxCounter(lightboxCurrentSlide);
      // Visible slide + neighbours load immediately
      loadLightboxSlidesNear(lightboxCurrentSlide, 2);
      // Background: load the rest with stagger so the network isn't crushed
      startProgressiveLightboxLoad();
    } else {
      // Desktop uses vertical scroll — load all slides up front
      loadAllLightboxSlides();
      requestAnimationFrame(() => {
        const scrollItems = lightboxContent.querySelectorAll('[data-lightbox-item]');
        if (scrollItems[lightboxCurrentSlide]) {
          let targetTop = 0;
          for (let i = 0; i < lightboxCurrentSlide; i++) {
            targetTop += scrollItems[i].offsetHeight;
          }
          lightboxContent.scrollTop = targetTop;
        }
      });
    }
  }

  function closeLightbox() {
    if (!lightbox) return;
    if (isInZoomMode()) exitZoomMode();
    lightbox.classList.remove('is-open');
    document.body.style.overflow = '';
    if (lightboxContent) {
      lightboxContent.scrollTop = 0;
      lightboxContent.scrollLeft = 0;
      lightboxContent.querySelectorAll('.is-zoomed').forEach(img => {
        img.classList.remove('is-zoomed');
      });
      // Clear inline styles so the next open starts fresh on whichever
      // viewport size is current.
      clearAllLightboxItemStyles();
    }
    lightboxCurrentSlide = 0;
  }

  function goToLightboxSlide(index) {
    if (index < 0) index = 0;
    if (index >= lightboxTotalSlides) index = lightboxTotalSlides - 1;
    // Always reset zoom when navigating to a new slide
    if (isInZoomMode()) exitZoomMode();
    lightboxCurrentSlide = index;

    updateLightboxItemPositions(index, true);
    loadLightboxSlidesNear(index, 2);
    updateLightboxCounter(index);
  }

  function updateLightboxCounter(index) {
    const currentEl = lightbox.querySelector('[data-lightbox-current]');
    if (currentEl) {
      currentEl.textContent = index + 1;
    }
  }

  function initLightbox() {
    if (!lightbox) return;

    const isMobile = () => window.innerWidth < 1024;

    // Close button — always works
    if (lightboxClose) {
      lightboxClose.addEventListener('click', (e) => {
        e.stopPropagation();
        closeLightbox();
      });
    }

    // Desktop overlay click closes
    const overlay = lightbox.querySelector('[data-lightbox-overlay]');
    if (overlay) {
      overlay.addEventListener('click', () => {
        if (!isMobile()) closeLightbox();
      });
    }

    if (!lightboxContent) {
      // Wire up Esc and bail — nothing else to do without content
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          if (isInZoomMode()) exitZoomMode();
          else if (lightbox.classList.contains('is-open')) closeLightbox();
        }
      });
      return;
    }

    // ---- Desktop: click handling ----
    // Click on a slide toggles zoom-to-fit. Click on whitespace closes the
    // lightbox. mouseDraggedDistance > 5 means it was a drag-pan ending —
    // don't toggle zoom in that case.
    lightboxContent.addEventListener('click', (e) => {
      if (isMobile()) return;
      if (mouseDraggedDistance > 5) {
        mouseDraggedDistance = 0;
        return;
      }
      const slide = e.target.closest('.product-lightbox__item');
      if (!slide) {
        if (e.target === lightboxContent ||
            e.target.hasAttribute('data-lightbox-inner') ||
            e.target.classList.contains('product-lightbox__inner')) {
          closeLightbox();
        }
        return;
      }
      if (zoomedSlide === slide) {
        exitZoomMode();
      } else {
        triggerZoomToFit(slide);
      }
    });

    // ---- Desktop: wheel zooms when in zoom mode ----
    // Outside zoom mode, wheel performs default vertical scroll through the
    // lightbox gallery — we don't hijack that.
    lightboxContent.addEventListener('wheel', (e) => {
      if (isMobile()) return;
      if (!isInZoomMode()) return;
      e.preventDefault();
      const factor = e.deltaY < 0 ? 1.1 : 0.9;
      const s = getZoomState(zoomedSlide);
      setSlideZoomScale(zoomedSlide, s.scale * factor);
    }, { passive: false });

    // ---- Desktop: mouse drag pans when zoomed ----
    lightboxContent.addEventListener('mousedown', (e) => {
      if (isMobile()) return;
      if (!isInZoomMode()) return;
      const slide = e.target.closest('.product-lightbox__item');
      if (slide !== zoomedSlide) return;
      panMouseStart = { x: e.clientX, y: e.clientY };
      mouseDraggedDistance = 0;
      e.preventDefault();
    });

    document.addEventListener('mousemove', (e) => {
      if (!panMouseStart || !zoomedSlide) return;
      const dx = e.clientX - panMouseStart.x;
      const dy = e.clientY - panMouseStart.y;
      mouseDraggedDistance += Math.abs(dx) + Math.abs(dy);
      panMouseStart.x = e.clientX;
      panMouseStart.y = e.clientY;
      panSlide(zoomedSlide, dx, dy);
    });

    document.addEventListener('mouseup', () => {
      panMouseStart = null;
    });

    // ---- Mobile: touch handlers (pinch | pan | swipe — mutually exclusive) ----
    lightboxContent.addEventListener('touchstart', (e) => {
      if (!isMobile()) return;
      const slide = e.target.closest('.product-lightbox__item');

      if (e.touches.length === 2 && slide) {
        // Two fingers → pinch-zoom on this slide
        pinchActive = true;
        pinchInitialDist = getPinchDistance(e.touches[0], e.touches[1]);
        pinchInitialScale = getZoomState(slide).scale;
        enterZoomMode(slide);
        panTouchActive = false;
        lbIsSwiping = false;
      } else if (e.touches.length === 1) {
        if (isInZoomMode() && slide === zoomedSlide) {
          // Single finger on the zoomed slide → pan
          panTouchActive = true;
          panTouchLastX = e.touches[0].clientX;
          panTouchLastY = e.touches[0].clientY;
          lbIsSwiping = false;
        } else {
          // Single finger, not zoomed → carousel swipe
          lbTouchStartX = e.touches[0].clientX;
          lbTouchMoveX = lbTouchStartX;
          lbIsSwiping = true;
          panTouchActive = false;
        }
      }
    }, { passive: true });

    lightboxContent.addEventListener('touchmove', (e) => {
      if (!isMobile()) return;
      if (e.touches.length === 2 && pinchActive && zoomedSlide) {
        const newDist = getPinchDistance(e.touches[0], e.touches[1]);
        const factor = newDist / pinchInitialDist;
        setSlideZoomScale(zoomedSlide, pinchInitialScale * factor);
      } else if (e.touches.length === 1 && panTouchActive && zoomedSlide) {
        const t = e.touches[0];
        const dx = t.clientX - panTouchLastX;
        const dy = t.clientY - panTouchLastY;
        panTouchLastX = t.clientX;
        panTouchLastY = t.clientY;
        panSlide(zoomedSlide, dx, dy);
      } else if (lbIsSwiping && e.touches.length === 1) {
        lbTouchMoveX = e.touches[0].clientX;
        const delta = lbTouchMoveX - lbTouchStartX;
        applyLightboxDragOffset(lightboxCurrentSlide, delta);
      }
    }, { passive: true });

    lightboxContent.addEventListener('touchend', (e) => {
      if (!isMobile()) return;
      if (pinchActive && e.touches.length < 2) pinchActive = false;
      if (panTouchActive && e.touches.length === 0) panTouchActive = false;
      if (lbIsSwiping && e.touches.length === 0) {
        lbIsSwiping = false;
        const diff = lbTouchStartX - lbTouchMoveX;
        const threshold = 50;
        let newIndex = lightboxCurrentSlide;
        if (diff > threshold && lightboxCurrentSlide < lightboxTotalSlides - 1) {
          newIndex = lightboxCurrentSlide + 1;
        } else if (diff < -threshold && lightboxCurrentSlide > 0) {
          newIndex = lightboxCurrentSlide - 1;
        }
        // goToLightboxSlide animates and snaps; if no swipe threshold was met,
        // newIndex === lightboxCurrentSlide and this is the snap-back path.
        goToLightboxSlide(newIndex);
      }
    }, { passive: true });

    // ---- Mobile: double-tap toggles zoom-to-fit ----
    let lastTapTime = 0;
    let lastTapTarget = null;
    lightboxContent.addEventListener('click', (e) => {
      if (!isMobile()) return;
      const slide = e.target.closest('.product-lightbox__item');
      if (!slide) return;
      const now = Date.now();
      if (now - lastTapTime < 350 && slide === lastTapTarget) {
        if (zoomedSlide === slide) {
          exitZoomMode();
        } else {
          triggerZoomToFit(slide);
        }
        e.preventDefault();
      }
      lastTapTime = now;
      lastTapTarget = slide;
    });

    // ---- Esc: exit zoom first, then close lightbox ----
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      if (isInZoomMode()) {
        exitZoomMode();
      } else if (lightbox.classList.contains('is-open')) {
        closeLightbox();
      }
    });
  }

  // =====================
  // ACCORDIONS
  // =====================
  function initAccordions() {
    accordions.forEach(accordion => {
      const trigger = accordion.querySelector('[data-accordion-trigger]');
      const body = accordion.querySelector('[data-accordion-body]');

      if (!trigger || !body) return;

      // Set initial height for open accordions
      if (accordion.classList.contains('is-open')) {
        body.style.maxHeight = body.scrollHeight + 'px';
      }

      trigger.addEventListener('click', () => {
        const isOpen = accordion.classList.contains('is-open');
        
        accordion.classList.toggle('is-open');
        trigger.setAttribute('aria-expanded', !isOpen);

        if (!isOpen) {
          body.style.maxHeight = body.scrollHeight + 'px';
        } else {
          body.style.maxHeight = '0';
        }

        // Re-sync gallery height after accordion animation
        setTimeout(syncGalleryHeight, 350);
      });
    });
  }

  // =====================
  // VARIANT SELECTION
  // =====================
  function initVariantSelection() {
    if (!product) return;

    const sizeButtons = section.querySelectorAll('[data-size-value]');
    sizeButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        const value = btn.dataset.sizeValue;
        const position = parseInt(btn.dataset.optionPosition);
        
        sizeButtons.forEach(b => {
          if (b.dataset.optionPosition === btn.dataset.optionPosition) {
            b.classList.remove('product-form__size--selected');
          }
        });
        btn.classList.add('product-form__size--selected');
        updateVariant(position, value);
      });
    });

    const swatchButtons = section.querySelectorAll('[data-swatch-value]');
    swatchButtons.forEach(btn => {
      btn.addEventListener('click', () => {
        const value = btn.dataset.swatchValue;
        const position = parseInt(btn.dataset.optionPosition);
        
        swatchButtons.forEach(b => {
          if (b.dataset.optionPosition === btn.dataset.optionPosition) {
            b.classList.remove('product-form__swatch--selected');
          }
        });
        btn.classList.add('product-form__swatch--selected');
        updateVariant(position, value);
      });
    });
  }

  function updateVariant(optionPosition, value) {
    if (!product || !product.variants) return;

    const selectedOptions = [];
    section.querySelectorAll('[data-size-value].product-form__size--selected, [data-swatch-value].product-form__swatch--selected').forEach(el => {
      const pos = parseInt(el.dataset.optionPosition);
      const val = el.dataset.sizeValue || el.dataset.swatchValue;
      selectedOptions[pos - 1] = val;
    });
    selectedOptions[optionPosition - 1] = value;

    const matchingVariant = product.variants.find(variant => {
      return variant.options.every((opt, index) => {
        return !selectedOptions[index] || opt === selectedOptions[index];
      });
    });

    if (matchingVariant) {
      updateProductDisplay(matchingVariant);
    }
  }

  function updateProductDisplay(variant) {
    if (variantInput) {
      variantInput.value = variant.id;
    }

    if (priceDisplay) {
      priceDisplay.innerHTML = formatMoney(variant.price);
    }

    if (stickyPrice) {
      stickyPrice.innerHTML = formatMoney(variant.price);
    }

    if (addToCartBtn && addToCartText) {
      if (variant.available) {
        addToCartBtn.disabled = false;
        addToCartText.textContent = 'Add to Cart';
      } else {
        addToCartBtn.disabled = true;
        addToCartText.textContent = 'Sold Out';
      }
    }

    const url = new URL(window.location);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }

  function formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return 'Rs. ' + amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  // =====================
  // STICKY BAR
  // =====================
  function initStickyBar() {
    if (!stickyBar || !addToCartBtn) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          stickyBar.classList.toggle('is-visible', !entry.isIntersecting);
        });
      },
      { threshold: 0 }
    );

    observer.observe(addToCartBtn);

    if (stickyAddToCart) {
      stickyAddToCart.addEventListener('click', () => {
        if (addToCartBtn) addToCartBtn.click();
      });
    }
  }

  // =====================
  // SIZE GUIDE MODAL
  // =====================
  function initSizeGuide() {
    const sizeGuideTrigger = section.querySelector('[data-size-guide-trigger]');
    const sizeGuideModal = section.querySelector('[data-size-guide-modal]');
    if (!sizeGuideTrigger || !sizeGuideModal) return;

    sizeGuideTrigger.addEventListener('click', (e) => {
      e.preventDefault();
      sizeGuideModal.classList.add('is-open');
      sizeGuideModal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    });

    sizeGuideModal.querySelectorAll('[data-size-guide-close]').forEach(btn => {
      btn.addEventListener('click', () => {
        sizeGuideModal.classList.remove('is-open');
        sizeGuideModal.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
      });
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && sizeGuideModal.classList.contains('is-open')) {
        sizeGuideModal.classList.remove('is-open');
        sizeGuideModal.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
      }
    });
  }

  // =====================
  // UTILITIES
  // =====================
  function debounce(func, wait) {
    let timeout;
    return function(...args) {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  }

  // =====================
  // INIT
  // =====================
  function init() {
    // Ensure body is scrollable on init (safety reset)
    document.body.style.overflow = '';
    
    initMobileCarousel();
    initDesktopGallery();
    initLightbox();
    initAccordions();
    initVariantSelection();
    initStickyBar();
    initSizeGuide();
    initGalleryHeightSync();
    updateProgress();
  }

  // Safety: Reset body overflow when page is restored from bfcache
  window.addEventListener('pageshow', () => {
    // Always reset on pageshow — handles bfcache and fresh loads
    const anyModalOpen = 
      (lightbox && lightbox.classList.contains('is-open')) ||
      (section && section.querySelector('.size-guide-modal.is-open'));
    if (!anyModalOpen) {
      document.body.style.overflow = '';
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();